import type { AwaitingPlayerSignal } from "@/lib/core/schemas/kernel";

/**
 * Frontend-boundary shape for MCP tool results, as served by the web
 * `/api/mcp` route. Mirrors `KernelAck`.
 */
export type McpToolResult = {
  status: "ACK" | "ERROR";
  correlationId: string;
  summary: string;
  error?: string;
  narrative: string;
  ui: string;
  data?: unknown;
  /**
   * Orchestration signal emitted by kernel systems when game state leaves
   * a player-input-pending shape (attack roll, save, damage reveal,
   * initiative, death save, reaction, pending choice). Frontend uses this to decide whether to fire
   * the nudge after a resolve (miss → fire; hit-with-damage → wait for
   * the reveal click to fire).
   */
  awaitingPlayer?: AwaitingPlayerSignal;
};

/**
 * Call a Kernel MCP tool via the Web API proxy.
 * Returns the KernelAck on success, throws on HTTP or tool errors.
 */
export async function callMcpTool(
  toolName: string,
  args: Record<string, unknown>,
  campaignId: string
): Promise<McpToolResult> {
  const res = await fetch("/api/mcp", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ toolName, args, campaignId }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "Network error" }));
    throw new Error(body.error ?? `MCP call failed: ${toolName}`);
  }

  const ack: McpToolResult = await res.json();
  if (ack.status === "ERROR") {
    throw new Error(ack.error ?? `MCP call failed: ${toolName}`);
  }

  return ack;
}
